/**
 * The press held down, which is how this game asks for the things it will not
 * put a permanent control on screen for: giving a game up, and pinning a peg.
 *
 * A hold has three moments and all three have to be answered or the gesture
 * misbehaves. It has to land while the finger is still down, because a gesture
 * you must let go of to find out about is one nobody explores. It has to be
 * called off by everything that means the finger was not being held there - a
 * tap, a scroll, a finger sliding away. And the click that arrives on release
 * has to be swallowed, or the hold's answer is immediately followed by the
 * tap's.
 *
 * A fourth thing arrives whether or not it is wanted: partway through a long
 * press the browser raises its own menu, the one a mouse asks for with the
 * right button. It is the same press, so it is the same gesture - see menu().
 */

/**
 * How long a press has to be held.
 *
 * Long enough that scrolling past something never trips it, short enough that
 * it is discoverable by holding rather than by waiting.
 */
export const LONG_PRESS_MS = 550;

export function createHold({ delay = LONG_PRESS_MS } = {}) {
    let timer = null;
    let landed = false;

    return {
        /**
         * Starts a hold. What it does when it lands is the caller's business;
         * an action returning false says nothing happened, which leaves the
         * click that follows alone.
         */
        start(action) {
            this.cancel();
            landed = false;
            timer = setTimeout(() => {
                timer = null;
                landed = action() !== false;
            }, delay);
        },

        /**
         * The same gesture arriving as a menu: the right button on a mouse, and
         * on a phone the menu the browser raises by itself partway through a
         * long press, unasked.
         *
         * One press, one answer. Whichever of the two lands first acts and the
         * other finds the press already answered - a second answer would only
         * ever undo the first, or land somewhere the first one moved on to.
         */
        menu(action) {
            if (landed) return false;
            this.cancel();
            landed = action() !== false;
            return landed;
        },

        /** Calls the hold off before it lands. */
        cancel() {
            if (timer) clearTimeout(timer);
            timer = null;
        },

        /**
         * Swallows the click ending a completed hold, and says it has, so the
         * caller can skip whatever a plain tap would have done.
         */
        click(event) {
            this.cancel();
            if (!landed) return false;
            landed = false;
            if (event && event.preventDefault) event.preventDefault();
            return true;
        },
    };
}
